import com.appsyde.shared.app.navigation.AppPaths
import com.appsyde.shared.di.initKoin
import com.appsyde.shared.app.presentation.SessionViewModel
import org.jetbrains.compose.web.renderComposable
import org.koin.core.context.GlobalContext
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.appsyde.shared.presentation.screen.screen.AppSydeLandingPage
import kotlinx.browser.window
import kotlinx.browser.window as kbWindow
import kotlinx.browser.localStorage
import com.appsyde.shared.clearSessionForLogout
import com.appsyde.shared.getAccessToken
import com.appsyde.shared.setAccessToken
import com.appsyde.shared.presentation.screen.screen.inbox.InboxPermissionsRequiredView
import com.appsyde.shared.presentation.screen.screen.ScreensDemoPage
import com.appsyde.shared.presentation.screen.screen.OAuthCompleteMessage
import com.appsyde.shared.presentation.StaticRouteOr
import com.appsyde.shared.presentation.LoggedInShell
import kotlinx.serialization.json.Json

fun main() {
    println("[WebMain] starting main")
    initKoin()
    println("[WebMain] koin started")

    renderComposable(rootElementId = "root") { WebApp() }
    println("[WebMain] renderComposable called")
}

@Composable
fun WebApp() {
    val pathname = remember { window.location.pathname }
    val sessionViewModel = GlobalContext.get().get<SessionViewModel>()
    val origin = remember { window.location.origin }
    val sessionState by sessionViewModel.sessionState.collectAsState()
    val loginUrl by sessionViewModel.loginUrl.collectAsState()

    // Join flow: backend may redirect here with #accessToken=...
    LaunchedEffect(Unit) {
        val hash = window.location.hash
        if (hash.startsWith("#accessToken=")) {
            val encoded = hash.removePrefix("#accessToken=").trim()
            if (encoded.isNotBlank()) {
                val decoded = (window.asDynamic().decodeURIComponent as (String) -> String)(encoded)
                setAccessToken(decoded)
                window.history.replaceState(null, "", window.location.pathname + window.location.search)
                sessionViewModel.refreshSession()
            }
        }
    }

    // OAuth dans le même onglet (redirection) pour éviter que le token soit perdu quand
    // le navigateur ouvre un nouvel onglet au lieu d'une popup (window.opener = null).
    LaunchedEffect(loginUrl) {
        val url = loginUrl
        if (!url.isNullOrBlank()) {
            try {
                window.location.href = url
            } catch (e: Throwable) {
                console.error("Failed to navigate to login URL", e)
            } finally {
                sessionViewModel.consumeLoginUrl()
            }
        }
    }

    DisposableEffect(Unit) {
        val listener: (dynamic) -> Unit = listener@ { event: dynamic ->
            try {
                val data = event.data
                if (data == null) return@listener
                val msg: OAuthCompleteMessage? = when {
                    data is String -> kotlin.runCatching { Json.decodeFromString<OAuthCompleteMessage>(data) }.getOrNull()
                    else -> if (data.type == "oauth_complete") OAuthCompleteMessage(
                        type = "oauth_complete",
                        accountId = (data.accountId as? String).orEmpty(),
                        accessToken = (data.accessToken as? String).orEmpty()
                    ) else null
                }
                if (msg != null && msg.type == "oauth_complete") {
                    if (msg.accountId.isNotBlank()) {
                        localStorage.setItem("accountId", msg.accountId)
                        println("[WebMain] Stored accountId=${msg.accountId} from oauth_complete")
                    }
                    if (msg.accessToken.isNotBlank()) {
                        setAccessToken(msg.accessToken)
                        println("[WebMain] Stored accessToken from oauth_complete")
                    }
                    sessionViewModel.refreshSession()
                }
            } catch (e: Throwable) {
                console.error("Error processing postMessage", e)
            }
        }

        kbWindow.addEventListener("message", listener)

        onDispose {
            try {
                kbWindow.removeEventListener("message", listener)
            } catch (_: Throwable) {}
        }
    }

    if (pathname.endsWith("/demo123") || pathname.endsWith("/demo123/")) {
        ScreensDemoPage()
        return
    }

    StaticRouteOr(pathname) {
        when (sessionState) {
             is SessionViewModel.SessionState.CheckingSession,
            is SessionViewModel.SessionState.LoggedOut -> AppSydeLandingPage(
                onSignup = { sessionViewModel.startSignup(origin) },
                onLogin = { sessionViewModel.startLogin(origin) },
                isCheckingSession = sessionState is SessionViewModel.SessionState.CheckingSession
            )

            is SessionViewModel.SessionState.NeedsConsent -> {
                InboxPermissionsRequiredView(
                    onGrantPermissions = { sessionViewModel.startReconnectGmail(origin) },
                    onLogout = { clearSessionForLogout(); sessionViewModel.logout() }
                )
            }

            is SessionViewModel.SessionState.LoggedIn -> LoggedInShell(
                sessionViewModel = sessionViewModel,
                origin = origin
            )
        }
    }
}
